/* ============================================
   RELATED WORKS COMPONENT
   The Creative Orchestra - GEMINI Framework
   Shows related works at the bottom of work pages
   ============================================ */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { works, worksArray } from '@data';
import { getWorkTheme } from '@utils/workThemes';
import { GEMINI_STAGES } from '@utils/constants';

/**
 * Related Works Component
 * Displays related works based on provided IDs or same stage
 * 
 * @param {Object} props
 * @param {string} props.currentWorkId - Current work ID to exclude
 * @param {Array} props.relatedWorkIds - Specific related work IDs
 * @param {number} props.maxItems - Maximum items to show
 */
const RelatedWorks = ({ 
  currentWorkId,
  relatedWorkIds,
  maxItems = 3,
  className = '',
}) => {
  // Get related works
  const relatedWorks = useMemo(() => {
    let worksList = [];

    // If specific IDs provided, use those
    if (relatedWorkIds && relatedWorkIds.length > 0) {
      worksList = relatedWorkIds
        .filter(id => id !== currentWorkId && works[id])
        .map(id => works[id])
        .slice(0, maxItems);
    }

    // If not enough, fill with same-stage works
    if (worksList.length < maxItems) {
      const currentWork = works[currentWorkId];
      const currentStage = currentWork?.stage;
      
      const sameStageWorks = worksArray
        .filter(w => 
          w.id !== currentWorkId && 
          w.stage === currentStage &&
          !worksList.find(rw => rw.id === w.id)
        )
        .slice(0, maxItems - worksList.length);
      
      worksList = [...worksList, ...sameStageWorks];
    }

    // If still not enough, add any other works
    if (worksList.length < maxItems) {
      const otherWorks = worksArray
        .filter(w => 
          w.id !== currentWorkId &&
          !worksList.find(rw => rw.id === w.id)
        )
        .slice(0, maxItems - worksList.length);
      
      worksList = [...worksList, ...otherWorks];
    }

    return worksList.slice(0, maxItems);
  }, [currentWorkId, relatedWorkIds, maxItems]);

  if (relatedWorks.length === 0) return null;

  return (
    <section className={`py-16 px-4 ${className}`}>
      <div className="max-w-6xl mx-auto">
        <h3 className="text-2xl font-semibold text-white mb-8 text-center">
          Explore More Works
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {relatedWorks.map((work, index) => (
            <RelatedWorkCard 
              key={work.id}
              work={work}
              index={index}
            />
          ))}
        </div>

        {/* View all works link */}
        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white
              transition-colors group"
          >
            View all works
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

/**
 * Related Work Card Component
 */
const RelatedWorkCard = ({ work, index }) => {
  const theme = getWorkTheme(work.id);
  const accentColor = theme?.colors?.accent || work.colorTheme?.accent || '#818CF8';

  // Extract RGB from hex
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result 
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : '129, 140, 248';
  };
  const accentRgb = hexToRgb(accentColor);

  return (
    <Link to={`/work/${work.id}`}>
      <motion.div
        className="group h-full p-6 rounded-2xl border transition-all duration-300"
        style={{
          background: `rgba(${accentRgb}, 0.05)`,
          borderColor: `rgba(${accentRgb}, 0.1)`,
        }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        whileHover={{ 
          y: -4,
          borderColor: `rgba(${accentRgb}, 0.3)`,
          boxShadow: `0 20px 40px rgba(${accentRgb}, 0.1)`,
        }}
      >
        {/* Orb */}
        <div 
          className="w-14 h-14 rounded-full flex items-center justify-center mb-4
            border transition-all duration-300
            group-hover:scale-110"
          style={{
            background: `linear-gradient(135deg, rgba(${accentRgb}, 0.3) 0%, rgba(${accentRgb}, 0.1) 100%)`,
            borderColor: `rgba(${accentRgb}, 0.3)`,
            boxShadow: `0 0 20px rgba(${accentRgb}, 0.2)`,
          }}
        >
          <Sparkles className="w-6 h-6" style={{ color: accentColor }} />
        </div>

        {/* Stage badge */}
        <div 
          className="inline-block text-xs font-medium px-2 py-0.5 rounded mb-2"
          style={{
            background: `rgba(${accentRgb}, 0.2)`,
            color: accentColor,
          }}
        >
          {work.stage}
        </div>

        {/* Name */}
        <h4 
          className="text-xl font-bold mb-1 transition-colors"
          style={{ color: 'white' }}
        >
          <span 
            className="group-hover:text-transparent group-hover:bg-clip-text
              transition-all duration-300"
            style={{
              backgroundImage: `linear-gradient(135deg, white, ${accentColor})`,
            }}
          >
            {work.name}
          </span>
        </h4>

        {/* Full name */}
        <p className="text-sm text-gray-400 mb-3">
          {work.fullName}
        </p>

        {/* Tagline */}
        <p className="text-sm text-gray-500 line-clamp-2">
          {work.tagline}
        </p>

        {/* Arrow */}
        <div className="mt-4 flex items-center gap-1 text-gray-500 
          group-hover:text-white transition-colors">
          <span className="text-sm">Learn more</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </motion.div>
    </Link>
  );
};

/**
 * Works By Stage Component
 * Shows all works grouped by GEMINI stage
 */
export const WorksByStage = ({ 
  currentWorkId,
  showAll = false,
  className = '',
}) => {
  const stages = Object.values(GEMINI_STAGES);

  return (
    <section className={`py-16 px-4 ${className}`}>
      <div className="max-w-6xl mx-auto">
        <h3 className="text-2xl font-semibold text-white mb-8 text-center">
          GEMINI Framework
        </h3>

        <div className="space-y-8">
          {stages.map((stage) => {
            const stageWorks = stage.works
              .filter(id => showAll || id !== currentWorkId)
              .map(id => works[id])
              .filter(Boolean);

            if (stageWorks.length === 0) return null;

            return (
              <div key={stage.letter}>
                {/* Stage header */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-lg font-bold px-3 py-1 rounded-lg
                    bg-white/10 text-white">
                    {stage.letter}
                  </span>
                  <span className="text-gray-400">{stage.name}</span>
                </div>

                {/* Works */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {stageWorks.map((work, index) => (
                    <SmallWorkCard 
                      key={work.id}
                      work={work}
                      isCurrent={work.id === currentWorkId}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/**
 * Small Work Card Component
 */
const SmallWorkCard = ({ work, isCurrent }) => {
  const theme = getWorkTheme(work.id);
  const accentColor = theme?.colors?.accent || '#818CF8';

  if (isCurrent) {
    return (
      <div 
        className="p-4 rounded-xl bg-white/5 border border-white/20 opacity-50"
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-full"
            style={{
              background: `linear-gradient(135deg, ${accentColor}40, ${accentColor}20)`,
            }}
          />
          <div>
            <p className="font-medium text-white">{work.name}</p>
            <p className="text-xs text-gray-500">Current</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link to={`/work/${work.id}`}>
      <motion.div
        className="p-4 rounded-xl bg-white/5 border border-white/10
          hover:bg-white/10 hover:border-white/20 transition-all group"
        whileHover={{ y: -2 }}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-full transition-transform group-hover:scale-110"
            style={{
              background: `linear-gradient(135deg, ${accentColor}40, ${accentColor}20)`,
              boxShadow: `0 0 10px ${accentColor}20`,
            }}
          />
          <div>
            <p className="font-medium text-white group-hover:text-indigo-300 transition-colors">
              {work.name}
            </p>
            <p className="text-xs text-gray-500 truncate max-w-[150px]">
              {work.tagline}
            </p>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

/**
 * Next Work Navigation
 * Shows a prominent link to the next work in sequence
 */
export const NextWorkNav = ({ currentWorkId, className = '' }) => {
  const currentIndex = worksArray.findIndex(w => w.id === currentWorkId);
  const nextWork = currentIndex >= 0 && currentIndex < worksArray.length - 1
    ? worksArray[currentIndex + 1]
    : null;

  if (!nextWork) return null;

  const theme = getWorkTheme(nextWork.id);
  const accentColor = theme?.colors?.accent || '#818CF8';

  return (
    <section className={`py-12 px-4 ${className}`}>
      <div className="max-w-4xl mx-auto">
        <Link to={`/work/${nextWork.id}`}>
          <motion.div
            className="p-6 rounded-2xl border bg-white/5 border-white/10
              hover:bg-white/10 hover:border-white/20 transition-all group"
            whileHover={{ y: -4 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-500">Next Work</span>
                <h4 
                  className="text-2xl font-bold mt-1 group-hover:text-indigo-300 transition-colors"
                  style={{ color: accentColor }}
                >
                  {nextWork.name}
                </h4>
                <p className="text-gray-400 mt-1">{nextWork.fullName}</p>
              </div>
              <ArrowRight 
                className="w-8 h-8 text-gray-500 group-hover:text-white
                  group-hover:translate-x-2 transition-all"
              />
            </div>
          </motion.div>
        </Link>
      </div>
    </section>
  );
};

export default RelatedWorks;
